import { faRedo, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'

import { Button, Modal } from '@/components'
import { logout } from '@/store/modules/auth/actions'

import { ItemTitle, ItemValue,RefreshButton } from '../styles'
import { ErrorContainerProps,ErrorContainerState, ErrorType } from '../types'

export const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			logout,
		},
		dispatch,
	)

const initialState: ErrorContainerState = {
	open: false,
	timestamp: null,
	error: null,
	body: null,
	type: ErrorType.HARD,
}

class ErrorContainerComponent extends React.PureComponent<
	ErrorContainerProps,
	ErrorContainerState,
	React.PropsWithChildren
> {
	constructor(props: ErrorContainerProps) {
		super(props)

		this.state = initialState
	}

	static getDerivedStateFromError(error: Error) {
		return {
			open: true,
			error,
			timestamp: new Date(),
			type: ErrorType.HARD,
		}
	}

	componentDidMount() {
		window.addEventListener(
			'unhandledrejection',
			this.handlePromiseRejection.bind(this),
		)
	}

	componentWillUnmount() {
		window.removeEventListener(
			'unhandledrejection',
			this.handlePromiseRejection.bind(this),
		)
	}

	private async handlePromiseRejection({ reason }: PromiseRejectionEvent) {
		const error =
			reason instanceof Error ? reason : new Error('Unable to fetch data')

		const response = reason?.response as Response | undefined
		const rtkStatus = reason?.status as number | string | undefined
		const rtkData = reason?.data

		const isApiError = response instanceof Response || rtkStatus !== undefined

		this.setState({
			open: true,
			error,
			response,
			timestamp: new Date(),
			type: isApiError ? ErrorType.SOFT : ErrorType.HARD,
		})

		if (response instanceof Response) {
			try {
				const data = await response.text()

				try {
					const json = JSON.parse(data)

					this.setState({
						body: json.errorDescription || json.error || json.message || data,
					})
				} catch {
					this.setState({ body: data })
				}
			} catch (parseError) {
				console.warn('Failed to load response body', parseError)
			}
		} else if (rtkStatus !== undefined) {
			const body =
				typeof rtkData === 'string'
					? rtkData
					: rtkData?.errorDescription ||
						rtkData?.error ||
						rtkData?.message ||
						JSON.stringify(rtkData)

			this.setState({
				body,
				response: {
					url: reason?.meta?.request?.url || 'Unknown URL',
					status: typeof rtkStatus === 'number' ? rtkStatus : 0,
					statusText:
						typeof rtkStatus === 'string' ? rtkStatus : String(rtkStatus),
				} as Response,
			})
		}
	}

	private handleClose = () => {
		this.setState(initialState)
	}

	private handleRefresh = () => {
		window.location.reload()
	}


	render() {
		const { error, body, timestamp, type, open, response } = this.state
		const { children } = this.props
		const isErrorSoft = type === ErrorType.SOFT

		if (error && open) {
			return (
				<>
					<Modal
						open={open}
						closeOnEscape={isErrorSoft}
						closeOnBackdropClick={isErrorSoft}
						onClose={isErrorSoft ? this.handleClose : undefined}
						zIndex={1001}
						contentStyle={{
							width: 'auto',
							minWidth: '250px',
							maxWidth: '50%',
						}}
						header={
							<div className="flex items-center justify-between w-full">
								<span>
									{response
										? 'Error while communicating with server'
										: 'Runtime error'}
								</span>
								{isErrorSoft && (
									<button
										onClick={this.handleClose}
										className="ml-4 p-1 hover:bg-gray-200 rounded transition-colors"
										aria-label="Close"
									>
										<FontAwesomeIcon icon={faTimes} />
									</button>
								)}
							</div>
						}
						footer={
							<div className="flex gap-2 justify-end">
								{isErrorSoft && (
									<Button schema="warn" onClick={this.handleClose}>
										Dismiss
									</Button>
								)}
								<RefreshButton onClick={this.handleRefresh} icon={faRedo}>
									Reload page
								</RefreshButton>
							</div>
						}
					>
						<div>
							<ItemTitle>Timestamp</ItemTitle>
							<ItemValue>{timestamp && timestamp.toISOString()}</ItemValue>
							<ItemTitle>Version</ItemTitle>
							<ItemValue>{process.env.VERSION}</ItemValue>
							{response && (
								<>
									<ItemTitle>Url</ItemTitle>
									<ItemValue>{response.url}</ItemValue>
								</>
							)}
							<ItemTitle>Message</ItemTitle>
							<ItemValue>{error.message}</ItemValue>
							{response && (
								<>
									<ItemTitle>Status</ItemTitle>
									<ItemValue>
										{response.status} {response.statusText}
									</ItemValue>
									<ItemTitle>Body</ItemTitle>
									<ItemValue>{body}</ItemValue>
								</>
							)}
							{!response && (
								<>
									<ItemTitle>Stack</ItemTitle>
									<ItemValue>
										<pre>{error.stack}</pre>
									</ItemValue>
								</>
							)}
						</div>
					</Modal>
					{children}
				</>
			)
		}

		return children
	}
}

export const ErrorContainer = connect(
	null,
	mapDispatchToProps,
)(ErrorContainerComponent) as React.FC<React.PropsWithChildren>
